import { client } from '@fhs/client';
import { useServiceModeContext } from '@fhs-legacy/frontend/src/state/service-mode';
import { useStoreContext } from '@fhs-legacy/frontend/src/state/store';
import { ICartEntry } from '@rbi-ctg/menu';
import logger from 'utils/logger';

import type { SelectedPicker as ComboSelectedPicker } from '../state/combo-selection';
import type { MenuPickerContext } from '../state/menu-picker';
import type { CustomizationSelections } from '../types';
import { forEachCustomizationOptionInPickerAspectOption } from '../utils';

const convertCustomizationSelectionsToModifiersForCart = ({
  resolvedCustomizationSelections,
  selectedPickerAspectOption,
}: {
  resolvedCustomizationSelections: CustomizationSelections;
  selectedPickerAspectOption: MenuPickerContext['selectedPickerAspectOption'];
}) => {
  const trueDefaultCustomizationOptionKeySet = new Set<string>();
  forEachCustomizationOptionInPickerAspectOption(
    selectedPickerAspectOption,
    ({ customizationOption }) => {
      if (customizationOption.minAmount && customizationOption.displayType === 'RADIO') {
        // Don't exclude customizations if a minimum amount is set and the display type is radio
        // An example of this is 'Salad Dressing' for Side Salads in Combos
        return;
      }

      for (const o of customizationOption.options) {
        if (o.isDefault && o.key) {
          trueDefaultCustomizationOptionKeySet.add(o.id);
        }
      }
    }
  );

  const modifiers: Array<{ id: string; quantity: number }> = [];

  for (const customizationOptionKey in resolvedCustomizationSelections) {
    const selection = resolvedCustomizationSelections[customizationOptionKey];

    if (typeof selection === 'string') {
      if (trueDefaultCustomizationOptionKeySet.has(`${customizationOptionKey}:${selection}`)) {
        // exclude true details from payload so we don't end up with a huge
        // list of `No ${modifier}` modifiers listed in the cart.
        continue;
      }

      modifiers.push({ id: `${customizationOptionKey}:${selection}`, quantity: 1 });
      continue;
    }

    for (const customizationOptionOptionKey in selection) {
      const qty = selection[customizationOptionOptionKey];

      if (qty === 0) {
        continue;
      }

      modifiers.push({
        id: `${customizationOptionKey}:${customizationOptionOptionKey}`,
        quantity: qty,
      });
    }
  }

  return modifiers;
};

export function useTransformForAddToCart() {
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();
  const storeId = store?.number;

  return async ({
    itemId,
    quantity,

    selectedPickerAspectOption,
    resolvedCustomizationSelections,

    isCombo = false,
    pickerSelections,
    comboSelectedPickers = {},
  }: {
    itemId: string;
    quantity: number;

    selectedPickerAspectOption: MenuPickerContext['selectedPickerAspectOption'];
    resolvedCustomizationSelections: CustomizationSelections;

    isCombo?: boolean;
    pickerSelections: MenuPickerContext['pickerSelections'];

    comboSelectedPickers?: Record<string, ComboSelectedPicker>;
  }) => {
    if (!serviceMode || !storeId) {
      // @TODO Do something about this situation?
      return;
    }

    const comboItems: Array<{
      slotId: string;
      itemId: string;
      price: number;
      modifiersJson: Array<{
        id: string;
        quantity: number;
      }>;
    }> = [];

    for (const [slotId, comboSelectedPicker] of Object.entries(comboSelectedPickers ?? {})) {
      if (!comboSelectedPicker.id) {
        continue;
      }

      comboItems.push({
        slotId,
        itemId: comboSelectedPicker.id,
        price: comboSelectedPicker.priceCents,
        modifiersJson: convertCustomizationSelectionsToModifiersForCart({
          selectedPickerAspectOption: comboSelectedPicker.selectedPickerAspectOption ?? null,
          resolvedCustomizationSelections:
            comboSelectedPicker.resolvedCustomizationSelections ?? {},
        }),
      });
    }

    if (isCombo && comboItems.length === 0) {
      throw new Error('Cannot convert combo item without combo selections');
    }

    const { data: cartEntry, errors } = await client.queries.convertMenuItemFormat({
      storeId,
      serviceMode,
      itemId,
      quantity,
      isCombo,
      comboItemsJson: JSON.stringify(comboItems),
      modifiersJson: JSON.stringify(
        convertCustomizationSelectionsToModifiersForCart({
          selectedPickerAspectOption,
          resolvedCustomizationSelections,
        })
      ),
      pickerSelections: JSON.stringify(pickerSelections),
    });

    if (errors || !cartEntry) {
      const message = '[Amplify Client]: queries.convertMenuItemFormat - server error';
      logger.error({ error: errors, message });
      throw new Error(message);
    }

    const reified = reifyCartEntry(cartEntry);

    return reified as unknown as ICartEntry;
  };
}

function reviver(key: string, value: unknown) {
  if ((key === 'children' || key === 'pickerSelections') && typeof value === 'string') {
    return JSON.parse(value, reviver);
  }

  return value;
}

// 'convertMenuItemFormat' Endpoint is sending back 'children' and 'pickerSelections'
// still in JSON format. We need to fully parse them before adding to cart
const reifyCartEntry = (obj: Record<string, unknown>) => {
  const copy = { ...obj };

  if ('children' in obj && typeof obj.children === 'string') {
    copy.children = JSON.parse(obj.children, reviver);
  }

  if ('pickerSelections' in obj && typeof obj.pickerSelections === 'string') {
    copy.pickerSelections = JSON.parse(obj.pickerSelections, reviver);
  }

  return copy;
};
